import { z } from 'zod'
import { FieldType } from './fieldTypeEnum'
import { InputFieldBase } from '../Fields/types'
import { PhoneNumberUtil } from 'google-libphonenumber'

enum ErrorMessages {
  DEFAULT = 'Der sket en fejl',
  EMAIL = 'Angiv venligst en gyldig email adresse',
  PHONE = 'Telefonnummeret skal bestå af 8 cifre',
  PHONE_DK_MAX = 'Telefonnummeret kan højst indeholde 8 tal',
  PHONE_INT_MAX = 'Telefonnummeret kan højst indeholde 15 tal',
  ONLYNUMBERS = 'Venligst skriv tal',
  INVALIDNUMBER = 'Ugyldigt nummer',
  REQUIRED = 'Obligatorisk felt',
  MESSAGE_MIN = 'Beskeden skal bestå af mindst 10 tegn.',
  NAME = 'Beklager! Feltet kan kun indeholde bogstaver',
  TOOMANYNAMES = 'Beklager! Der skal være ét fornavn og ét efternavn',
  TOOFEWLETTER = 'Skriv mere end 3 bogstaver',
  ACCOUNTNUMBER_MAX = 'Kundenummeret er for langt, Angiv højst 9 cifre',
  ACCOUNTNUMBER_MIN = 'Kundenummeret skal bestå af mindst 9 cifre',
  INVOICENUMBER_MAX = 'Faktura er for langt, Angiv højst 3 cifre',
  INVOICENUMBER_MIN = 'Faktura skal bestå af mindst 3 cifre',
  FAKTURA_KONTONUMMER_STARTSWITH = 'Starter fx med 6, 5, 3 eller 2'
}

type ITestObject = {
  message: string
  test: (val: unknown) => unknown
}

const NameValidation = /^[a-zA-Z-\s\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u00FF]+$/
const numberValidation = /^[0-9]+$/
const fakturaKontonummerValidation = /^\d*[6532]$/

const phoneUtil = PhoneNumberUtil.getInstance()
const validatePhone = (phone: string) => {
  try {
    return phoneUtil.isValidNumber(phoneUtil.parseAndKeepRawInput(phone))
  } catch (error) {
    return false
  }
}

function getFieldValidation(
  type: FieldType,
  isRequired: boolean,
  customValidation?: ITestObject,
  requiredText?: string,
): z.Schema<unknown> {
  z.setErrorMap((_issue, _ctx) => {
    return { message: requiredText ?? ErrorMessages.REQUIRED }
  })

  const validationMap = new Map<FieldType, z.Schema<unknown>>([
    [
      FieldType.ADDRESS,
      z.any().refine(value => value && typeof value === 'object', {
        message: ErrorMessages.TOOFEWLETTER,
      }),
    ],
    [
      FieldType.CVR,
      z.any().refine(value => value && typeof value === 'object', {
        message: ErrorMessages.TOOFEWLETTER,
      }),
    ],
    [
      FieldType.EMAIL,
      z
        .string()
        .min(3, {
          message: ErrorMessages.TOOFEWLETTER,
        })
        .email({ message: ErrorMessages.EMAIL }),
    ],
    [
      FieldType.SELECT,
      z.union([
        z.string().min(1, {
          message: ErrorMessages.REQUIRED,
        }),
        z.number().min(1, {
          message: ErrorMessages.REQUIRED,
        }),
      ]),
    ],
    [
      FieldType.CHECKBOX,
      z.literal(true, {
        required_error: ErrorMessages.REQUIRED,
      }),
    ],
    [
      FieldType.SWITCH,
      z.literal(true, {
        required_error: ErrorMessages.REQUIRED,
      }),
    ],
    [
      FieldType.PASSWORD,
      z.string().refine(value => !!value, {
        message: requiredText ?? ErrorMessages.REQUIRED,
      }),
    ],
    [FieldType.CHECKBOXGROUP, z.any()],
    [FieldType.MESSAGE, z.string().min(10, { message: ErrorMessages.MESSAGE_MIN })],
    [
      FieldType.PHONE_DK,
      z
        .string()
        .min(8, { message: ErrorMessages.PHONE })
        .max(8, { message: ErrorMessages.PHONE_DK_MAX })
        .refine(value => value.match(numberValidation), {
          message: ErrorMessages.ONLYNUMBERS,
        }),
    ],
    [
      FieldType.PHONE_INTERNATIONAL,
      z.string().refine(value => validatePhone(value), {
        message: ErrorMessages.INVALIDNUMBER,
      }),
    ],
    [FieldType.TEXT, z.string().min(1, { message: ErrorMessages.REQUIRED })],
    [
      FieldType.NAME,
      z.string().refine(value => NameValidation.test(value), {
        message: ErrorMessages.NAME,
      }),
    ],
    [
      FieldType.ACCOUNTNUMBER,
      z
        .string()
        .min(9, { message: ErrorMessages.ACCOUNTNUMBER_MIN })
        .max(9, { message: ErrorMessages.ACCOUNTNUMBER_MAX })
        .refine(value => value.match(numberValidation), {
          message: ErrorMessages.ONLYNUMBERS,
        }),
    ],
    [
      FieldType.FAKTURA_KONTONUMMER,
      z
        .string()
        .min(9, { message: ErrorMessages.ACCOUNTNUMBER_MIN })
        .max(9, { message: ErrorMessages.ACCOUNTNUMBER_MAX })
        .refine(value => value[0]?.match(fakturaKontonummerValidation ), {
          message: ErrorMessages.FAKTURA_KONTONUMMER_STARTSWITH,
        })
        .refine(value => value.match(numberValidation), {
          message: ErrorMessages.ONLYNUMBERS,
        }),
    ],
    [
      FieldType.INVOICENUMBER,
      z.string().refine(value => value.match(numberValidation), {
        message: ErrorMessages.ONLYNUMBERS,
      }),
    ],
    [
      FieldType.FILEUPLOAD,
      z.array(z.any()).refine(value => value && Array.isArray(value) && value.length > 0, {
        message: 'Venligst tilføj en fil',
      }),
    ],
  ])

  let validation = validationMap.get(type) ?? z.any()

  if (customValidation) {
    validation = validation.refine(customValidation.test, {
      message: customValidation.message,
    })
  }

  return isRequired ? validation : z.any()
}

export default function getValidationSchema(fields: InputFieldBase[]) {
  const schema: Record<string, z.ZodType<any, any>> = {}

  fields?.forEach(currentField => {
    if (currentField.fieldType) {
      const fieldValidation = getFieldValidation(
        currentField.fieldType,
        currentField.required!,
        currentField.customValidation,
        currentField.requiredText,
      )

      schema[currentField.name!] = fieldValidation
    } else {
      schema[currentField.name!] = z.any().optional()
    }
  })

  const zodSchema = z.object(schema)
  return zodSchema
}
